/**
 * Project Management API Service
 */
import { apiClient } from './base';

// TypeScript Interfaces (matching backend Pydantic schemas)

export interface Project {
  project_id: string;
  company_id: string;
  project_name: string;
  timezone?: string;
  client?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  is_active?: boolean;
  date_create?: string | null;
  create_by?: string | null;
  date_last_modified?: string | null;
  modified_by?: string | null;
}

export interface ProjectListItem {
  project_id: string;
  project_name: string;
  client?: string;
  address?: string;
  company_id: string;
  is_active?: boolean;
  latitude?: string | null;
  longitude?: string | null;
  date_create?: string | null;
  create_by?: string | null;
}

export interface ProjectCreate {
  project_name: string;
  company_id: string;
  timezone?: string;
  client?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
}

export interface ProjectUpdate {
  project_name: string;
  timezone?: string;
  client?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
}

export interface ProjectSearchParams {
  company_id?: string;
  project_name?: string;
  client?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}

export interface ProjectListResponse {
  items: ProjectListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ProjectUser {
  user_id: string;
  user_name: string;
  email: string;
  role: 'admin' | 'view';
}

export interface ProjectUserAssignment {
  user_id: string;
  // Note: Role comes from user's UserType, not stored in Project2User table
}

export interface ProjectContact {
  contact_id: string;
  contact_name: string;
  email?: string;
  phone?: string;
}

export interface ProjectProduct {
  product_id: string;
  code: string;
  description: string;
  strength_grade?: number;
  /** The mix design's own company, whose strength unit its grade is in
   *  (null when the mix design is gone; absent from older responses). */
  company_id?: string | null;
}

export interface ProjectLogger {
  logger_id: string;
  serial_number: string;
  device_type?: string;
}

// API Service
const projectsService = {
  /**
   * Create a new project
   */
  createProject: async (data: ProjectCreate): Promise<Project> => {
    const response = await apiClient.post('/projects/', data);
    return response.data;
  },

  /**
   * Get all projects for a company
   */
  listProjects: async (companyId?: string): Promise<ProjectListItem[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/projects/', { params });
    return response.data;
  },

  /**
   * Search projects with pagination
   */
  searchProjects: async (params: ProjectSearchParams): Promise<ProjectListResponse> => {
    const response = await apiClient.get('/projects/search', { params });
    return response.data;
  },

  /**
   * Distinct client names across the company's projects (for autocomplete)
   */
  getClients: async (): Promise<string[]> => {
    const response = await apiClient.get('/projects/clients');
    return response.data;
  },

  /**
   * Get a single project by ID
   */
  getProject: async (projectId: string): Promise<Project> => {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  },

  /**
   * Update a project
   */
  updateProject: async (projectId: string, data: ProjectUpdate): Promise<Project> => {
    const response = await apiClient.put(`/projects/${projectId}`, data);
    return response.data;
  },

  /**
   * Activate or deactivate a project (deactivation replaces deletion for
   * non-super-admin users; linked pours are preserved)
   */
  setProjectActive: async (projectId: string, isActive: boolean): Promise<Project> => {
    const response = await apiClient.patch(`/projects/${projectId}/active`, { is_active: isActive });
    return response.data;
  },

  /**
   * Delete a project (cascade deletes all related data; super admin only)
   */
  deleteProject: async (projectId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}`);
  },

  // User Management

  /**
   * Get users assigned to a project
   */
  getProjectUsers: async (projectId: string): Promise<ProjectUser[]> => {
    const response = await apiClient.get(`/projects/${projectId}/users`);
    return response.data;
  },

  /**
   * Assign a user to a project
   */
  assignUser: async (projectId: string, data: ProjectUserAssignment): Promise<ProjectUser> => {
    const response = await apiClient.post(`/projects/${projectId}/users`, data);
    return response.data;
  },

  /**
   * Remove a user from a project
   */
  removeUser: async (projectId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/users/${userId}`);
  },

  // Contact Management

  /**
   * Get contacts assigned to a project
   */
  getProjectContacts: async (projectId: string): Promise<ProjectContact[]> => {
    const response = await apiClient.get(`/projects/${projectId}/contacts`);
    return response.data;
  },

  /**
   * Assign a contact to a project
   */
  assignContact: async (projectId: string, contactId: string): Promise<void> => {
    await apiClient.post(`/projects/${projectId}/contacts`, { contact_id: contactId });
  },

  /**
   * Remove a contact from a project
   */
  removeContact: async (projectId: string, contactId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/contacts/${contactId}`);
  },

  // Product Management

  /**
   * Get products assigned to a project
   */
  getProjectProducts: async (projectId: string): Promise<ProjectProduct[]> => {
    const response = await apiClient.get(`/projects/${projectId}/products`);
    return response.data;
  },

  /**
   * Assign a product to a project
   */
  assignProduct: async (projectId: string, productId: string): Promise<void> => {
    await apiClient.post(`/projects/${projectId}/products`, { product_id: productId });
  },

  /**
   * Remove a product from a project
   */
  removeProduct: async (projectId: string, productId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/products/${productId}`);
  },

  // Logger Management

  /**
   * Get loggers assigned to a project
   */
  getProjectLoggers: async (projectId: string): Promise<ProjectLogger[]> => {
    const response = await apiClient.get(`/projects/${projectId}/loggers`);
    return response.data;
  },

  /**
   * Assign a logger to a project
   */
  assignLogger: async (projectId: string, loggerId: string): Promise<void> => {
    await apiClient.post(`/projects/${projectId}/loggers`, { logger_id: loggerId });
  },

  /**
   * Remove a logger from a project
   */
  removeLogger: async (projectId: string, loggerId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/loggers/${loggerId}`);
  },
};

export default projectsService;

